import React, { useState, useEffect, useMemo } from "react";
import { TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import PropTypes from "prop-types";

import TrackingError from "./TrackingError";
import FlightBezier from "./FlightBezier";
import TrackingMarker from "./TrackingMarker";
import RouteLine from "./RouteLine";
import TruckMarkers from "./TruckMarkers";

const CARTO_BASEMAP_KEY = import.meta.env.REACT_APP_CARTO_BASEMAP_KEY;

function TrackingMap({ data, shipmentRoute }) {
  const [mapExpanded] = useState(false);
  const map = useMap();

  const [pacificCrossed, setPacificCrossed] = useState(false);

  const flightCoords = useMemo(() => {
    let origin;
    let destination;
    if (shipmentRoute?.coordinates.length) {
      origin = shipmentRoute.coordinates[0].slice().reverse();
      destination = shipmentRoute.coordinates.at(-1).slice().reverse();
    } else if (data?.schedule?.schedule_stops.length) {
      const pickupData = data?.schedule?.schedule_stops[0];
      const deliveryData = data?.schedule?.schedule_stops.at(-1);
      if (pickupData?.pickup_latitude && pickupData?.pickup_longitude) {
        origin = [pickupData.pickup_latitude, pickupData.pickup_longitude];
      }
      if (deliveryData?.delivery_latitude && deliveryData?.delivery_longitude) {
        destination = [deliveryData.delivery_latitude, deliveryData.delivery_longitude];
      }
    }
    if (origin && destination) {
      return {
        origin,
        destination,
      };
    }
    return null;
  }, [data, shipmentRoute]);

  /**
   * achieved this by passing attributionControl={false} to the MapContainer
   * if in future that stops working, we can use the useEffect below.
   */
  // useEffect(() => {
  //   // Gets rid of leaflet tile pane in the right lower corner
  //   const controlAttribute = document.getElementsByClassName("leaflet-control-attribution");
  //   if (controlAttribute.length > 0) controlAttribute[0].style.display = "none";
  // }, []);

  // Resizes the map when expanding/collapsing.
  useEffect(() => {
    map.invalidateSize({
      pan: false,
    });
  }, [mapExpanded]);

  // Centers the map based on the coordinates of the origin and destination markers.
  useEffect(() => {
    const coords = flightCoords;
    if (coords) {
      if ((coords.origin[1] > 60 && coords.destination[1] < -70) || (coords.origin[1] < -70 && coords.destination[1] > 60)) {
        setPacificCrossed(true);
        if (coords.origin[1] < 0) coords.origin = [coords.origin[0], coords.origin[1] + 360];
        if (coords.destination[1] < 0) coords.destination = [coords.destination[0], coords.destination[1] + 360];
      }
      const bounds = [coords.origin, coords.destination];
      map.fitBounds(bounds, {
        padding: [25, 25],
      });
    }
  }, [flightCoords]);

  return (
    <div style={{ position: "relative", height: mapExpanded ? 600 : 250 }}>
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url={`https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${CARTO_BASEMAP_KEY}`}
      />
      {flightCoords ? (
        <>
          {shipmentRoute?.coordinates.length ? (
            <RouteLine shipmentRoute={shipmentRoute} pacificCrossed={pacificCrossed} />
          ) : (
            <FlightBezier
              coords={flightCoords}
              status={data.organization.shipment_status}
              freightModeType={data.service?.freight_mode_type}
            />
          )}
          <TrackingMarker data={data} coords={flightCoords.origin} type="origin" pacificCrossed={pacificCrossed} />
          <TrackingMarker data={data} coords={flightCoords.destination} type="destination" pacificCrossed={pacificCrossed} />
          <TruckMarkers />
        </>
      ) : (
        <TrackingError mapExpanded={mapExpanded} />
      )}
      {/* <FontAwesomeIcon
        icon={faChevronDown}
        className="ml-auto mr-2 cursor-pointer"
        onClick={() => {
          setMapExpanded(!mapExpanded);
        }}
        style={{
          position: "absolute",
          bottom: "10px",
          right: "10px",
          zIndex: "1000",
          transition: "all .5s",
          transform: mapExpanded ? "rotate(180deg)" : "rotate(0deg)",
        }}
      /> */}
    </div>
  );
}

TrackingMap.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  shipmentRoute: PropTypes.instanceOf(Object),
};

TrackingMap.defaultProps = {
  shipmentRoute: null,
};

export default TrackingMap;
